import { useQuery } from '@tanstack/react-query'
import { GitHubUser } from '@/components/Autocomplete.tsx'
import { trpc } from '@/trpcReact'

export type UseGithubUsersParams = {
  debouncedSearchValue: string
}

export const useGithubUsers = ({ debouncedSearchValue }: UseGithubUsersParams) => {
  const trpcUtils = trpc.useUtils()

  return useQuery<GitHubUser[]>({
    queryKey: ['githubUsers', debouncedSearchValue],
    queryFn: async () => {
      // First try the direct GitHub API
      const response = await fetch(
        `https://api.github.com/search/users?q=${debouncedSearchValue}+in:login+type:user&per_page=5`,
      )

      if (!response.ok) {
        // If error (likely rate limited), use our server endpoint as fallback
        // Call the tRPC endpoint imperatively
        return await trpcUtils.client.searchGithubUsers.query({
          searchValue: debouncedSearchValue,
        })
      }

      const data = await response.json()
      return (data.items || []).map((user: any) => ({
        value: user.login,
        label: user.login,
        avatarUrl: user.avatar_url,
      }))
    },
    enabled: debouncedSearchValue.length >= 1, // Only enable query for valid inputs
    retry: (failureCount, error) => {
      // Only retry once
      return failureCount < 1
    },
    retryDelay: 1000, // Wait 1 second before retrying
  })
}
